import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Dropdown, Checkbox, Button } from 'flowbite-react';

// Redux hooks and API
import { useFetchQuestionnaireListQuery } from '../../redux/api/questionnaireApi';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// Utils
import { getLocalizedValueFromMultiLangValues } from '../../utils/getLocalizedValueFromMultiLangValues';

// Interfaces
import { IQuestionnaire } from '../../common/interfaces/Questionnaire.interface';

// Components
import { DateFilters } from '../Shared/DateFilters/DateFilters';

interface WeeklyLogAnalyticsFiltersProps {
  filters: {
    questionnaireId: string;
    questionId: string;
    answerIds: string[];
    startDate: Date;
    endDate: Date;
  };
  onFiltersChange: (filters: {
    questionnaireId: string;
    questionId: string;
    answerIds: string[];
    startDate: Date;
    endDate: Date;
  }) => void;
}

export const WeeklyLogAnalyticsFilters: React.FC<
  WeeklyLogAnalyticsFiltersProps
> = ({ filters, onFiltersChange }) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const { data: questionnairesData } = useFetchQuestionnaireListQuery();
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<IQuestionnaire | null>(null);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<string>('');

  // Update selected questionnaire when filters change
  useEffect(() => {
    if (questionnairesData?.data && filters.questionnaireId) {
      const questionnaire = questionnairesData.data.find(
        (q) => q.id === filters.questionnaireId
      );
      setSelectedQuestionnaire(questionnaire || null);
    }
  }, [filters.questionnaireId, questionnairesData]);

  const handleQuestionnaireChange = (questionnaireId: string) => {
    const questionnaire = questionnairesData?.data.find(
      (q) => q.id === questionnaireId
    );
    setSelectedQuestionnaire(questionnaire || null);
    onFiltersChange({
      ...filters,
      questionnaireId,
      questionId: '',
      answerIds: [],
    });
  };

  const handleQuestionChange = (questionId: string) => {
    onFiltersChange({
      ...filters,
      questionId,
      answerIds: [],
    });
  };

  const handleAnswerToggle = (answerId: string) => {
    const newAnswerIds = filters.answerIds.includes(answerId)
      ? filters.answerIds.filter((id) => id !== answerId)
      : [...filters.answerIds, answerId];

    onFiltersChange({
      ...filters,
      answerIds: newAnswerIds,
    });
  };

  const handleQuickDateChange = (dates: {
    startDate: Date;
    endDate: Date;
    filterId?: string;
  }) => {
    if (dates.filterId) {
      setSelectedQuickFilter(dates.filterId);
    } else {
      setSelectedQuickFilter('');
    }
    onFiltersChange({
      ...filters,
      startDate: dates.startDate,
      endDate: dates.endDate,
    });
  };

  const selectedQuestion = selectedQuestionnaire?.questions.find(
    (q) => q.id?.toString() === filters.questionId
  );

  return (
    <div className="space-y-4 rounded-2xl bg-white p-6 shadow">
      <h3 className="text-lg font-semibold text-gray-900">
        {t('analytics.filters.title')}
      </h3>

      {/* Questionnaire Selection */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('analytics.filters.selectQuestionnaire')}
        </label>
        <Select
          value={filters.questionnaireId}
          onChange={(e) => handleQuestionnaireChange(e.target.value)}
          className="w-full"
        >
          <option value="">{t('analytics.filters.chooseQuestionnaire')}</option>
          {questionnairesData?.data.map((questionnaire) => (
            <option key={questionnaire.id} value={questionnaire.id}>
              {getLocalizedValueFromMultiLangValues(questionnaire.body)}
            </option>
          ))}
        </Select>
      </div>

      {/* Question Selection */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('analytics.filters.selectQuestion')}
        </label>
        <Select
          value={filters.questionId}
          onChange={(e) => handleQuestionChange(e.target.value)}
          disabled={!selectedQuestionnaire}
          className="w-full"
        >
          <option value="">{t('analytics.filters.chooseQuestion')}</option>
          {selectedQuestionnaire?.questions.map((question) => (
            <option key={question.id} value={question.id}>
              {getLocalizedValueFromMultiLangValues(question.body)}
            </option>
          ))}
        </Select>
      </div>

      {/* Answer Multi-Select Dropdown */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('analytics.filters.selectAnswers')}
        </label>
        <Dropdown
          className="max-h-60 overflow-y-auto"
          dismissOnClick={false}
          arrowIcon={false}
          inline
          disabled={!selectedQuestion}
          label={
            <Button
              size="md"
              className={`btn flex w-full items-center justify-between ${isDarkMode ? 'btn-dark' : 'btn-light'} ${!selectedQuestion ? 'cursor-not-allowed opacity-50' : ''}`}
              as={'span'}
            >
              <span>
                {filters.answerIds.length > 0
                  ? `${filters.answerIds.length} ${t('analytics.filters.answersSelected')}`
                  : t('analytics.filters.chooseAnswers')}
              </span>
            </Button>
          }
        >
          {selectedQuestion?.options?.map((option) => (
            <Dropdown.Item
              key={option.id}
              className={`flex items-center gap-3 hover:bg-gray-100${
                filters.answerIds.includes(option.id.toString()) &&
                'bg-gray-100'
              }`}
              onClick={() => handleAnswerToggle(option.id.toString())}
            >
              <Checkbox
                id={`answer-${option.id}`}
                checked={filters.answerIds.includes(option.id.toString())}
                onChange={() => handleAnswerToggle(option.id.toString())}
              />
              <span>{getLocalizedValueFromMultiLangValues(option.body)}</span>
            </Dropdown.Item>
          ))}
        </Dropdown>
      </div>

      {/* Date Filters */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('analytics.filters.dateRange')}
        </label>
        <DateFilters
          startDate={filters.startDate}
          endDate={filters.endDate}
          onDateChange={handleQuickDateChange}
          selectedFilter={selectedQuickFilter}
        />
      </div>
    </div>
  );
};
